"use client";

import Image from "next/image";
import NextLink from "next/link";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import Card from "@/components/common/Card";
import Link from "@/components/common/Link";
import { PATH } from "@/constants/path";
import { useBatchImpressionTracking } from "@/hooks/useImpressionTracking";
import { trackEvent } from "@/utils/analytics";

interface BlogCardData {
  slug: string;
  title: string;
  content: string;
  thumbnail: string;
}

// 2026-04-22 데이터 재선정 (실측 lead·체류·다채널 기준)
const BLOG_CARDS: BlogCardData[] = [
  {
    slug: "ai-recruiter-context-based-screening",
    title: "서치라이트 AI 리크루터 ‘루피’",
    content: "JD를 15개 지표로 쪼개고, 100명을 5분 만에 평가하는 원리를 공개해요.",
    thumbnail: "https://assets.searchright.net/2026/03/342342.png",
  },
  {
    slug: "how-ai-recruiter-builds-talent-list",
    title: "서치라이트 리스트 제작 과정",
    content: "실제 채용 사례로 ‘AI는 속도, 사람은 정확도’ 6단계 프로세스를 공개해요.",
    thumbnail: "https://assets.searchright.net/2026/03/Group-131.png",
  },
  {
    slug: "clevel-senior-pm-recruiting",
    title: "서치라이트 아웃바운드 채용 전략",
    content: "공고·헤드헌팅 안 되던 C레벨·시니어를 회신률 37.4%로 뽑은 비결을 소개해요.",
    thumbnail: "https://assets.searchright.net/2026/03/daswqrfqe32r.png",
  },
];

function BlogSection() {
  const path = usePathname();

  const impressionEvents = useMemo(
    () =>
      BLOG_CARDS.map((card, idx) => ({
        eventName: "blog_section_impression",
        params: { blog_slug: card.slug, card_position: idx + 1 },
      })),
    [],
  );

  const sectionRef = useBatchImpressionTracking<HTMLDivElement>(impressionEvents);

  const handleCardClick = (slug: string, position: number) => {
    trackEvent("blog_card_click", path, { blog_slug: slug, card_position: position });
  };

  return (
    <div ref={sectionRef} className="mt-20 flex flex-col items-center justify-center gap-6 px-side">
      <div className="grid w-full max-w-[1200px] grid-cols-1 gap-4 tablet:grid-cols-3">
        {BLOG_CARDS.map((card, idx) => (
          <NextLink
            key={card.slug}
            href={`${PATH.BLOG}/${card.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleCardClick(card.slug, idx + 1)}
          >
            <Card>
              <div className="relative aspect-video w-full overflow-hidden">
                <Image
                  src={card.thumbnail}
                  alt={card.title}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 33vw"
                />
              </div>
              <div className="px-4 pb-6 pt-4">
                <div className="mb-3 text-24 font-semibold">{card.title}</div>
                <div className="whitespace-pre-line text-18 text-gray">{card.content}</div>
              </div>
            </Card>
          </NextLink>
        ))}
      </div>
      <Link href={PATH.BLOG} text="더 많은 글 읽어보기" target="_blank" rel="noopener noreferrer" />
    </div>
  );
}

export default BlogSection;
